import { AnimatePresence } from "framer-motion";
import { Archive, ArrowRight, GitBranchPlus, Radio, Split, Trash2, Undo2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import type { Creative, Hypothesis } from "@lovmia/shared";
import { ApiError } from "../api";
import { useAppState } from "../state";
import { Button, ConfirmDialog, Menu, Modal, Select, type MenuAction } from "./Ui";

/**
 * След крео во внешнем мире — то же деление, что на сервере (domain.ts,
 * creativeFootprint). «Измеренное» крео (спенд или вердикт) нельзя переименовать:
 * его имя стало фактом в кабинете. Заведённое без спенда сервер отзовёт сам по
 * дороге — здесь достаточно предупредить об этом человека.
 */
export function creativeFootprint(creative: Creative) {
  const measured = creative.metrics.spend > 0 || Boolean(creative.verdict);
  const registered = Boolean(creative.creativeUid);
  return { measured, registered, movable: !measured };
}

/**
 * Управление одним крео из модалки крео и из галереи гипотезы: подвинуть по
 * фазам доски, перенести в другую гипотезу, отделить в новую, удалить. Один
 * компонент на обе витрины — чтобы набор действий и их правила не разъезжались.
 *
 * Движение по фазам — рабочий ход оператора доски (его разрешает и сервер:
 * `decision` с `test2`/`archive` и `mark-live` открыты роли user), поэтому оно
 * есть у всех. Решения по составу семейства — за админом; сервер проверяет роль
 * сам. Двигается ровно одно исполнение: гипотеза и её братья остаются на месте.
 */
export function CreativeControls({ creative, family, onDone, className }: {
  creative: Creative;
  family: Hypothesis;
  /** Зовётся, когда крео исчезло из этой гипотезы (удалено или переехало) — закрыть его карточку. */
  onDone?: () => void;
  className?: string;
}) {
  const { data, act, notify } = useAppState();
  const [dialog, setDialog] = useState<"move" | "split" | "delete" | "backlog" | null>(null);
  const [busy, setBusy] = useState(false);
  const admin = data.user.role === "admin";
  const footprint = creativeFootprint(creative);

  /** Фазовый переход одной ручкой: тост об успехе, серверный текст об отказе. */
  const phase = async (path: string, success: string, body?: unknown) => {
    if (busy) return;
    setBusy(true);
    try {
      await act(`/api/creatives/${creative.id}${path}`, { method: "POST", ...(body ? { body: JSON.stringify(body) } : {}) }, success);
    } catch (error) {
      notify(error instanceof ApiError ? error.message : "Не удалось перенести крео", undefined, "danger");
    } finally {
      setBusy(false);
    }
  };
  const decide = (action: "test2" | "archive", success: string) => phase("/decision", success, { action });

  const actions: MenuAction[] = [];
  // Тот же путь, что кнопка «В live» на карточке: в Media Library уезжает файл.
  if (creative.state === "ready_for_upload" && creative.file) {
    actions.push({
      label: "Отправить в открут (тест 1)", icon: <Radio size={14} />,
      onSelect: () => void phase("/mark-live", `${creative.id} ушло в Media Library`)
    });
  }
  // Скейл-прогон одного исполнения: бюджет теста 2 считается от текущего
  // спенда, поэтому вход в фазу возможен только один раз (сервер отобьёт повтор).
  if (["live", "paused_manual", "verdict_pending"].includes(creative.state)) {
    actions.push({
      label: "Перенести в открут (тест 2)", icon: <ArrowRight size={14} />,
      onSelect: () => void decide("test2", `${creative.id} в тесте 2 — запускайте скейл-прогон`)
    });
  }
  if (admin && footprint.movable) {
    // Обратный ход по доске: измеренное крео сервер не пустит (имя стало
    // фактом), поэтому пункт видно только у того, что ещё можно вернуть.
    actions.push({ label: "Вернуть в бэклог", icon: <Undo2 size={14} />, onSelect: () => setDialog("backlog") });
  }
  // Убрать из ротации, не трогая гипотезу: цифры и имя остаются фактом, крео
  // просто перестаёт быть живым. Для измеренного это замена возврата в бэклог.
  if (creative.state !== "archived") {
    actions.push({
      label: "Отправить в архив", icon: <Archive size={14} />,
      onSelect: () => void decide("archive", `${creative.id} в архиве`)
    });
  }
  if (admin && footprint.movable) {
    actions.push({ label: "Перенести в другую гипотезу…", icon: <Split size={14} />, onSelect: () => setDialog("move") });
    actions.push({ label: "Отделить в новую гипотезу…", icon: <GitBranchPlus size={14} />, onSelect: () => setDialog("split") });
  }
  if (admin) {
    actions.push({ label: "Удалить из гипотезы", icon: <Trash2 size={14} />, tone: "danger", onSelect: () => setDialog("delete") });
  }

  return <>
    <Menu actions={actions} className={className} label={`Действия по ${creative.id}`} />
    <AnimatePresence>
      {dialog === "backlog" && <ReturnCreativeDialog creative={creative} close={() => setDialog(null)} onDone={onDone} />}
      {dialog === "move" && <MoveCreativesDialog family={family} creatives={[creative]} mode="move" close={() => setDialog(null)} onDone={onDone} />}
      {dialog === "split" && <MoveCreativesDialog family={family} creatives={[creative]} mode="split" close={() => setDialog(null)} onDone={onDone} />}
      {dialog === "delete" && <DeleteCreativesDialog creatives={[creative]} close={() => setDialog(null)} onDone={onDone} />}
    </AnimatePresence>
  </>;
}


/** Возврат одного исполнения в продакшн: залитое рано крео ждёт нового файла. */
export function ReturnCreativeDialog({ creative, close, onDone }: { creative: Creative; close: () => void; onDone?: () => void }) {
  const { act, notify } = useAppState();
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await act(`/api/creatives/${creative.id}/backlog`, { method: "POST", body: JSON.stringify({}) }, `${creative.id} вернулось в продакшн`);
      close();
      onDone?.();
    } catch (error) {
      notify(error instanceof ApiError ? error.message : "Не удалось вернуть крео", undefined, "danger");
    } finally {
      setLoading(false);
    }
  };

  return <ConfirmDialog
    title={`Вернуть ${creative.id} в продакшн?`}
    confirmLabel="Вернуть"
    loading={loading}
    onConfirm={submit}
    onClose={close}
    description={<>
      <p>Крео уйдёт с открутки обратно в продакшн и будет ждать новый файл. Имя объявления и номер сохранятся.</p>
      {creative.creativeUid && <p>Оно заведено в кабинете — сначала будет отозвано из Media Library (кабинет удалит локализацию и файл).</p>}
    </>}
  />;
}

/**
 * Перенос крео (одного или партии) в существующую гипотезу или в новую —
 * разделение семейства. Одна форма на оба случая: человек, который выносит
 * три вариации из H-025, ещё не знает, есть ли у них уже дом на доске.
 */
export function MoveCreativesDialog({ family, creatives, mode, close, onDone }: {
  family: Hypothesis;
  creatives: Creative[];
  mode: "move" | "split";
  close: () => void;
  /** После успешного переноса: крео больше нет в исходной гипотезе. */
  onDone?: () => void;
}) {
  const { data, act, notify } = useAppState();
  const [target, setTarget] = useState<"new" | string>(mode === "split" ? "new" : "");
  const [title, setTitle] = useState("");
  const [statement, setStatement] = useState(family.statement);
  const [loading, setLoading] = useState(false);
  const ids = creatives.map((creative) => creative.id);
  const registered = creatives.filter((creative) => creative.creativeUid);
  const blocked = creatives.filter((creative) => !creativeFootprint(creative).movable);
  const label = ids.length === 1 ? ids[0] : `${ids.length} крео`;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (blocked.length) return;
    setLoading(true);
    try {
      if (target === "new") {
        const created = await act<Hypothesis>(`/api/hypotheses/${family.id}/split`, {
          method: "POST",
          body: JSON.stringify({ creativeIds: ids, title: title.trim(), ...(statement.trim() !== family.statement ? { statement: statement.trim() } : {}) })
        });
        notify(`${label} отделено в ${created.id} · ${created.title ?? ""}`, registered.length ? "Заведённые крео отозваны из Media Library — после проверки нажмите «В live»" : undefined, "success");
      } else {
        await act(`/api/hypotheses/${target}/creatives/move-in`, { method: "POST", body: JSON.stringify({ creativeIds: ids }) });
        notify(`${label} → ${target}`, registered.length ? "Заведённые крео отозваны из Media Library — после проверки нажмите «В live»" : undefined, "success");
      }
      close();
      onDone?.();
    } catch (error) {
      notify(error instanceof ApiError ? error.message : "Не удалось перенести", undefined, "danger");
    } finally {
      setLoading(false);
    }
  };

  return <Modal onClose={close}><form className="dialog-form" onSubmit={submit}>
    <span className="eyebrow">{family.id} · {label}</span>
    <h2>{mode === "split" ? "Отделить в новую гипотезу" : "Перенести крео"}</h2>
    <p>Крео получит следующий номер в новой гипотезе и новое имя объявления. В исходной гипотезе номера соседей сдвинутся.</p>
    <ul className="pick-list">
      {creatives.map((creative) => <li key={creative.id}><span className="mono">{creative.id}</span> {creative.variationTitle ?? ""}{creative.creativeUid && <em> · заведено в кабинете — будет отозвано</em>}</li>)}
    </ul>
    {blocked.length > 0 && <p className="field-note field-note--danger">
      {blocked.map((creative) => creative.id).join(", ")}: уже открутило бюджет или получило вердикт — имя стало фактом в кабинете, перенести нельзя. Заведите новое крео в целевой гипотезе.
    </p>}
    <label className="field field--full"><span>Куда</span>
      <Select value={target} onChange={(event) => setTarget(event.target.value)}>
        {mode === "move" && <option value="">Выберите гипотезу…</option>}
        <option value="new">+ Новая гипотеза (копия карточки {family.id})</option>
        {data.hypotheses.filter((item) => item.id !== family.id).map((item) => <option key={item.id} value={item.id}>{item.id} · {(item.title ?? item.statement).slice(0, 70)}</option>)}
      </Select>
    </label>
    {target === "new" && <>
      <label className="field field--full"><span>Имя механики новой гипотезы</span>
        <input autoFocus required minLength={2} maxLength={80} value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Стоп скроллинг: аниме" />
      </label>
      <label className="field field--full"><span>Утверждение (по умолчанию — как у {family.id})</span>
        <textarea required minLength={8} value={statement} onChange={(event) => setStatement(event.target.value)} />
      </label>
      <p className="field-note">Боль, желание, угол, источник, автор, приоритет, KPI, тон и шаблон наследуются от {family.id}.</p>
    </>}
    <div className="dialog-actions">
      <Button type="button" variant="ghost" onClick={close}>Отмена</Button>
      <Button type="submit" variant="primary" loading={loading} disabled={!target || blocked.length > 0 || (target === "new" && title.trim().length < 2)}>
        {target === "new" ? "Отделить" : "Перенести"}
      </Button>
    </div>
  </form></Modal>;
}

/**
 * Удаление крео (одного или партии). Мягкое уходит с перенумерацией соседей;
 * измеренное — только принудительно, и диалог прямо говорит, что при этом
 * останется в кабинете и что пропадёт с доски. Слово «force» здесь не UI-
 * галочка, а осознанное подтверждение: у такого крео своя, более тяжёлая кнопка.
 */
export function DeleteCreativesDialog({ creatives, close, onDone }: {
  creatives: Creative[];
  close: () => void;
  onDone?: () => void;
}) {
  const { act, notify } = useAppState();
  const [loading, setLoading] = useState(false);
  const measured = creatives.filter((creative) => creativeFootprint(creative).measured);
  const registered = creatives.filter((creative) => creative.creativeUid && !creativeFootprint(creative).measured);
  const label = creatives.length === 1 ? creatives[0]!.id : `${creatives.length} крео`;

  const remove = async () => {
    setLoading(true);
    try {
      for (const creative of creatives) {
        const force = creativeFootprint(creative).measured;
        await act(`/api/creatives/${creative.id}${force ? "?force=true" : ""}`, { method: "DELETE" });
      }
      notify(`${label} удалено — номера соседей сдвинуты`, undefined, "success");
      close();
      onDone?.();
    } catch (error) {
      notify(error instanceof ApiError ? error.message : "Не удалось удалить крео", undefined, "danger");
    } finally {
      setLoading(false);
    }
  };

  return <ConfirmDialog
    title={`Удалить ${label}?`}
    confirmLabel={measured.length ? "Удалить принудительно" : "Удалить"}
    loading={loading}
    onConfirm={remove}
    onClose={close}
    description={<>
      <ul className="pick-list">
        {creatives.map((creative) => <li key={creative.id}><span className="mono">{creative.id}</span> {creative.variationTitle ?? ""}</li>)}
      </ul>
      <p>Крео исчезнет с доски, номера мягких соседей сдвинутся вниз. Файл в хранилище останется. Удаление необратимо.</p>
      {registered.length > 0 && <p>{registered.map((creative) => creative.id).join(", ")}: заведено в кабинете, но ничего не открутило — сначала будет отозвано из Media Library (кабинет удалит локализацию и файл).</p>}
      {measured.length > 0 && <p className="field-note field-note--danger">
        {measured.map((creative) => creative.id).join(", ")}: уже открутило бюджет или получило вердикт. Объявление в кабинете останется без владельца, а его измерения исчезнут с доски — гейт гипотезы и медианы это почувствуют. Если нужно просто убрать из ротации — лучше «Архив».
      </p>}
    </>}
  />;
}
